import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addQuestionsToSection, addSection, deleteSection, getAssessments, getBanks, getDrawPreview, getTags, getVersionQuestions, removeQuestionFromSection, reviseQuestion, searchQuestions, setDraw, setMustInclude,
} from '../api/endpoints'
import type { DrawMode, SectionView } from '../api/types'
import { ErrorNotice, Pagination } from '../components'
import Drawer from '../Drawer'
import HowItWorks from '../HowItWorks'
import { Notice } from '../Toast'
import QuestionDialog from './QuestionDialog'

const MODES: { value: DrawMode; label: string }[] = [
  { value: 'ALL', label: 'Everyone gets every question in the pool' },
  { value: 'RANDOM', label: 'Each candidate gets a random number of them' },
  { value: 'BY_DIFFICULTY', label: 'Each candidate gets a set number of easy, medium and difficult ones' },
]

/** What each candidate gets from a section, in words, so the setup can be checked at a glance. */
function drawSummary(s: SectionView): string {
  const pool = s.questions.length
  if (s.drawMode === 'RANDOM') return `${Math.min(s.drawCount ?? pool, pool)} of ${pool}, chosen at random for each candidate`
  if (s.drawMode === 'BY_DIFFICULTY') return `${s.drawEasy} easy + ${s.drawMedium} medium + ${s.drawDifficult} difficult, chosen at random for each candidate`
  return `all ${pool}`
}

/** Builds a draft test from the question banks: pick the pool for each section, mark must-include questions, decide how many each candidate gets. */
export default function ComposePage() {
  const versionId = Number(useParams().versionId)
  const queryClient = useQueryClient()
  const assessments = useQuery({ queryKey: ['assessments'], queryFn: getAssessments })
  const sections = useQuery({ queryKey: ['version-questions', versionId], queryFn: () => getVersionQuestions(versionId) })
  const [preview, setPreview] = useState(false)
  const check = useQuery({ queryKey: ['draw-preview', versionId, preview, sections.dataUpdatedAt], queryFn: () => getDrawPreview(versionId, 3), enabled: preview })
  const [notice, setNotice] = useState<string | null>(null)
  const [picking, setPicking] = useState<string | null>(null)
  const [editing, setEditing] = useState<{ id: number; bank: number } | null>(null)
  const [newSection, setNewSection] = useState({ code: '', name: '', weight: '' })

  const found = assessments.data?.flatMap((a) => a.versions.map((v) => ({ a, v }))).find((x) => x.v.id === versionId)
  const draft = found?.v.status === 'DRAFT'
  const refresh = () => { void queryClient.invalidateQueries({ queryKey: ['version-questions', versionId] }); void queryClient.invalidateQueries({ queryKey: ['assessments'] }) }
  const add = useMutation({
    mutationFn: () => addSection(versionId, { code: newSection.code.trim(), name: newSection.name.trim(), weight: newSection.weight === '' ? null : Number(newSection.weight) }),
    onSuccess: () => { setNotice('Section added.'); setNewSection({ code: '', name: '', weight: '' }); refresh() },
  })

  return (
    <div>
      <p className="muted" style={{ margin: 0 }}><Link to="/content">Content</Link> /</p>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1>{found ? `${found.a.name} v${found.v.versionNo}: questions and draw` : 'Questions and draw'}</h1>
        <button type="button" onClick={() => setPreview(true)} disabled={check.isFetching}>{check.isFetching ? 'Drawing…' : 'Preview draws'}</button>
      </div>
      <HowItWorks id="compose" title="How to build a test from a bank" lead="Each section has a pool of questions. You decide how many of the pool every candidate gets, so two candidates can sit different papers."
        steps={[
          { icon: 'upload', title: '1. Build the pool', text: 'Choose "Add from a question bank" and tick the questions you want in this section. For example, pick 40 of the 50 in your bank.' },
          { icon: 'schedule', title: '2. Set the draw', text: 'Choose "random" and enter 15: each candidate then gets 15 of the 40, different for each person. Or choose a set number of easy, medium and difficult questions to keep papers balanced.' },
          { icon: 'check', title: '3. Pin must-haves', text: 'Tick "Must include" on questions every candidate has to get. They always count toward the number you set.' },
          { icon: 'shield', title: '4. Preview and publish', text: '"Preview draws" shows three sample papers and lists anything that would stop publishing. Scores are scaled to what each candidate was given, so papers of the same size and balance stay fair.' },
        ]} />
      {!draft && found && <Notice tone="info">This version is published, so it is read-only. To change it, go back to Content and start a new draft version.</Notice>}
      {notice && <Notice tone="ok">{notice}</Notice>}
      <ErrorNotice error={sections.error ?? assessments.error ?? add.error} />

      {preview && check.data && (
        <section className="card" aria-label="Preview of sample papers">
          <div className="row" style={{ justifyContent: 'space-between' }}><h2>Sample papers</h2><button type="button" onClick={() => setPreview(false)}>Close</button></div>
          {check.data.problems.length > 0 ? <><Notice tone="error">Fix these before publishing:</Notice><ul>{check.data.problems.map((p) => <li key={p}>{p}</li>)}</ul></> : <Notice tone="ok">The draw settings are valid.</Notice>}
          <div className="table-wrap"><table>
            <thead><tr><th>Paper</th>{sections.data?.map((s) => <th key={s.code}>{s.name}</th>)}<th className="num">Total</th><th className="num">Same as paper 1</th></tr></thead>
            <tbody>{check.data.samples.map((p) => (
              <tr key={p.number}><td>Paper {p.number}</td>
                {sections.data?.map((s) => { const d = p.sections.find((x) => x.section === s.code); return <td key={s.code}>{d ? `${d.questionIds.length} (${d.easy}E · ${d.medium}M · ${d.difficult}D)` : '–'}</td> })}
                <td className="num">{p.total}</td><td className="num">{p.number === 1 ? '–' : `${p.sharedWithFirst} questions`}</td></tr>
            ))}</tbody>
          </table></div>
          <p className="muted">E, M and D are easy, medium and difficult. Each preview is a fresh random draw, so run it again to see more.</p>
        </section>
      )}

      {sections.data?.map((s) => (
        <SectionCard key={s.id} section={s} versionId={versionId} editable={Boolean(draft)} onChanged={refresh} onAdd={() => setPicking(s.code)}
          onEdit={(id, bank) => setEditing({ id, bank })} onNotice={setNotice} />
      ))}

      {draft && (
        <form className="card" onSubmit={(e) => { e.preventDefault(); add.mutate() }}>
          <h2>Add a section</h2>
          <div className="toolbar">
            <label>Code<input value={newSection.code} onChange={(e) => setNewSection({ ...newSection, code: e.target.value })} placeholder="e.g. REASONING" maxLength={24} /></label>
            <label>Name<input value={newSection.name} onChange={(e) => setNewSection({ ...newSection, name: e.target.value })} placeholder="e.g. Reasoning" /></label>
            <label>Weight<input type="number" min={0} step="any" value={newSection.weight} onChange={(e) => setNewSection({ ...newSection, weight: e.target.value })} style={{ width: 90 }} /></label>
            <button type="submit" disabled={!newSection.code.trim() || !newSection.name.trim() || add.isPending}>Add section</button>
          </div>
        </form>
      )}
      {picking && <PickDialog versionId={versionId} section={picking} used={new Set(sections.data?.flatMap((s) => s.questions.map((q) => q.id)))} onClose={() => setPicking(null)}
        onDone={(m) => { setPicking(null); setNotice(m); refresh() }} />}
      {editing && <QuestionDialog bankId={editing.bank} questionId={editing.id} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); setNotice('Question saved.'); refresh() }} />}
    </div>
  )
}

function SectionCard({ section: s, versionId, editable, onChanged, onAdd, onEdit, onNotice }: {
  section: SectionView; versionId: number; editable: boolean; onChanged: () => void; onAdd: () => void; onEdit: (id: number, bank: number) => void; onNotice: (m: string) => void
}) {
  const [mode, setMode] = useState<DrawMode>(s.drawMode)
  const [count, setCount] = useState(String(s.drawCount ?? ''))
  const [easy, setEasy] = useState(String(s.drawEasy))
  const [medium, setMedium] = useState(String(s.drawMedium))
  const [difficult, setDifficult] = useState(String(s.drawDifficult))
  const pool = s.questions.length
  const by = (d: string) => s.questions.filter((q) => q.difficulty === d).length
  const pinned = s.questions.filter((q) => q.mustInclude).length
  const saveDraw = useMutation({
    mutationFn: () => setDraw(versionId, s.code, { mode, count: mode === 'RANDOM' && count !== '' ? Number(count) : null, easy: Number(easy) || 0, medium: Number(medium) || 0, difficult: Number(difficult) || 0 }),
    onSuccess: () => { onNotice(`Draw for ${s.name} saved.`); onChanged() },
  })
  const pin = useMutation({ mutationFn: (v: { id: number; on: boolean }) => setMustInclude(versionId, s.code, v.id, v.on), onSuccess: onChanged })
  const remove = useMutation({ mutationFn: (id: number) => removeQuestionFromSection(versionId, s.code, id), onSuccess: onChanged })
  const revise = useMutation({ mutationFn: (id: number) => reviseQuestion(versionId, s.code, id), onSuccess: (r) => { onNotice('A copy was made in this draft. Edit it now; the published version is untouched.'); onChanged(); onEdit(r.id, 0) } })
  const dropSection = useMutation({ mutationFn: () => deleteSection(versionId, s.code), onSuccess: onChanged })

  return (
    <section className="card">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2>{s.name} <span className="badge">{s.code}</span></h2>
        {editable && <div className="row"><button type="button" onClick={onAdd}>Add from a question bank</button>{pool === 0 && <button type="button" className="danger" onClick={() => dropSection.mutate()}>Remove section</button>}</div>}
      </div>
      <p className="muted">Pool: <b>{pool}</b> questions ({by('EASY')} easy · {by('MEDIUM')} medium · {by('DIFFICULT')} difficult{pool - by('EASY') - by('MEDIUM') - by('DIFFICULT') > 0 ? ` · ${pool - by('EASY') - by('MEDIUM') - by('DIFFICULT')} without a difficulty` : ''}) · must include: {pinned}.
        {' '}Each candidate gets: <b>{drawSummary(s)}</b>.</p>
      <ErrorNotice error={saveDraw.error ?? pin.error ?? remove.error ?? revise.error ?? dropSection.error} />

      {editable && (
        <form className="toolbar" onSubmit={(e) => { e.preventDefault(); saveDraw.mutate() }} aria-label={`Draw settings for ${s.name}`}>
          <label>How many each candidate gets<select value={mode} onChange={(e) => setMode(e.target.value as DrawMode)}>{MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select></label>
          {mode === 'RANDOM' && <label>Number of questions<input type="number" min={1} max={Math.max(pool, 1)} value={count} onChange={(e) => setCount(e.target.value)} style={{ width: 90 }} required /></label>}
          {mode === 'BY_DIFFICULTY' && <>
            <label>Easy<input type="number" min={0} value={easy} onChange={(e) => setEasy(e.target.value)} style={{ width: 70 }} /></label>
            <label>Medium<input type="number" min={0} value={medium} onChange={(e) => setMedium(e.target.value)} style={{ width: 70 }} /></label>
            <label>Difficult<input type="number" min={0} value={difficult} onChange={(e) => setDifficult(e.target.value)} style={{ width: 70 }} /></label>
          </>}
          <button type="submit" className="primary" disabled={saveDraw.isPending}>{saveDraw.isPending ? 'Saving…' : 'Save draw'}</button>
        </form>
      )}

      <div className="table-wrap"><table>
        <thead><tr><th>Question</th><th>Difficulty</th><th>Tags</th><th>Must include</th>{editable && <th />}</tr></thead>
        <tbody>
          {s.questions.map((q) => (
            <tr key={q.id}>
              <td className="q-cell">{q.body.length > 120 ? `${q.body.slice(0, 120)}…` : q.body}{q.retired && <> <span className="badge warn">retired</span></>}</td>
              <td>{q.difficulty ? q.difficulty.toLowerCase() : <span className="muted">not set</span>}</td><td>{q.tags.join(', ')}</td>
              <td><input type="checkbox" aria-label={`Must include: ${q.body.slice(0, 40)}`} checked={q.mustInclude} disabled={!editable || pin.isPending} onChange={(e) => pin.mutate({ id: q.id, on: e.target.checked })} /></td>
              {editable && <td><div className="row">
                <button type="button" onClick={() => (q.locked ? revise.mutate(q.id) : onEdit(q.id, 0))} title={q.locked ? 'Used in a published version: this makes a copy in the draft to edit' : undefined}>{q.locked ? 'Edit a copy' : 'Edit'}</button>
                <button type="button" onClick={() => remove.mutate(q.id)} disabled={remove.isPending}>Remove</button>
              </div></td>}
            </tr>
          ))}
          {pool === 0 && <tr><td colSpan={5} className="muted">No questions yet. {editable ? 'Choose "Add from a question bank".' : ''}</td></tr>}
        </tbody>
      </table></div>
    </section>
  )
}

/** Pick questions from any bank into a section. Questions already in this version cannot be picked again. */
function PickDialog({ versionId, section, used, onClose, onDone }: { versionId: number; section: string; used: Set<number>; onClose: () => void; onDone: (message: string) => void }) {
  const banks = useQuery({ queryKey: ['banks'], queryFn: getBanks })
  const tags = useQuery({ queryKey: ['tags'], queryFn: getTags })
  const [bank, setBank] = useState<number | null>(null)
  const [q, setQ] = useState('')
  const [tag, setTag] = useState('')
  const [difficulty, setDifficulty] = useState('')
  const [page, setPage] = useState(0)
  const [chosen, setChosen] = useState<Set<number>>(new Set())
  const bankId = bank ?? banks.data?.[0]?.id ?? null
  const list = useQuery({ queryKey: ['pick', bankId, q, tag, difficulty, page], queryFn: () => searchQuestions(bankId!, { q, tag, difficulty, status: 'active', page, size: 25 }), enabled: bankId !== null })
  const add = useMutation({
    mutationFn: () => addQuestionsToSection(versionId, section, [...chosen]),
    onSuccess: (r) => onDone(`Added ${r.added} question${r.added === 1 ? '' : 's'} to ${section}.${r.skipped.length > 0 ? ` ${r.skipped.length} could not be added (already in this version or retired).` : ''}`),
  })
  const rows = list.data?.items ?? []
  const free = rows.filter((r) => !used.has(r.id))
  const toggle = (id: number) => setChosen((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const filter = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(0) }

  return (
    <Drawer title={`Add questions to ${section}`} width={920} onClose={onClose} onSubmit={() => undefined}
      footer={<>
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className="primary" disabled={chosen.size === 0 || add.isPending} onClick={() => add.mutate()}>{add.isPending ? 'Adding…' : `Add ${chosen.size} question${chosen.size === 1 ? '' : 's'}`}</button>
      </>}>
        <p className="muted">Tick the questions to put in this section's pool. Your choices are kept while you search and move between pages.</p>
        <div className="toolbar">
          <label>Bank<select value={bankId ?? ''} onChange={(e) => { setBank(Number(e.target.value)); setPage(0) }}>{banks.data?.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.active})</option>)}</select></label>
          <label>Search<input value={q} onChange={(e) => filter(setQ)(e.target.value)} /></label>
          <label>Tag<select value={tag} onChange={(e) => filter(setTag)(e.target.value)}><option value="">All</option>{tags.data?.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}</select></label>
          <label>Difficulty<select value={difficulty} onChange={(e) => filter(setDifficulty)(e.target.value)}><option value="">All</option><option value="EASY">Easy</option><option value="MEDIUM">Medium</option><option value="DIFFICULT">Difficult</option></select></label>
        </div>
        {banks.data?.length === 0 && <Notice tone="info">There are no question banks yet. Create one under Question banks first.</Notice>}
        <ErrorNotice error={list.error ?? add.error} />
        <div className="table-wrap"><table>
          <thead><tr><th><input type="checkbox" aria-label="Select all on this page" checked={free.length > 0 && free.every((r) => chosen.has(r.id))}
            onChange={(e) => setChosen((c) => { const n = new Set(c); free.forEach((r) => (e.target.checked ? n.add(r.id) : n.delete(r.id))); return n })} /></th><th>Question</th><th>Difficulty</th><th>Tags</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td><input type="checkbox" aria-label={`Pick: ${r.body.slice(0, 40)}`} disabled={used.has(r.id)} checked={chosen.has(r.id)} onChange={() => toggle(r.id)} /></td>
                <td className="q-cell">{r.body.length > 130 ? `${r.body.slice(0, 130)}…` : r.body}{used.has(r.id) && <> <span className="badge">already in this version</span></>}</td>
                <td>{r.difficulty ? r.difficulty.toLowerCase() : <span className="muted">not set</span>}</td><td>{r.tags.join(', ')}</td>
              </tr>
            ))}
            {list.data && rows.length === 0 && <tr><td colSpan={4} className="muted">No questions match.</td></tr>}
          </tbody>
        </table></div>
        {list.data && <Pagination page={page} size={25} total={list.data.total} onPage={setPage} />}
    </Drawer>
  )
}
